// Pure model behind ScoreTrendChart (the "gelişim" score-over-time chart). Kept out of the component
// so the parts that can silently misreport a learner's or a branch's history — which points make the
// timeline, which series are drawable, and whether a missing score stays missing — are testable.

export interface TrendPoint {
  /** What the point IS — an assignment/exam name, or a week label. Shown as the tooltip title. */
  label: string;
  /** Epoch millis. Points without a usable timestamp cannot be placed and are dropped. */
  timestamp: number | null | undefined;
  /** 0–100. `null` means "no evidence", NOT zero — it is skipped, never plotted as a 0. */
  primary: number | null;
  benchmarks?: Record<string, number | null>;
}

export interface TrendBenchmarkDef {
  key: string;
  label: string;
  color: string;
}

export interface TrendSource {
  id: string;
  /** Names the SOURCE (e.g. "Şube ödevleri"). Used for the source-selector tab, and as the series
   *  name whenever several sources share the axis and telling them apart is what matters. */
  label: string;
  /** Names WHOSE SCORE the primary line is (e.g. "Şube puanı" / "Öğrenci puanı"), so that a lone
   *  series and its comparison lines are labelled on one axis (scope) instead of two (source for
   *  the primary, scope for the benchmarks).
   *
   *  Used only when the source is alone on the axis AND something outside the legend still names it
   *  — see `sourceNamedElsewhere` on {@link buildTrendChartModel}. Falls back to `label`. */
  primaryLabel?: string;
  color: string;
  points: TrendPoint[];
  /** Comparison lines available for this source (e.g. şube / sınıf averages). Optional per source:
   *  personalized assignments and mock exams carry no group comparators today. */
  benchmarkDefs?: TrendBenchmarkDef[];
}

export interface TrendDataset {
  label: string;
  data: (number | null)[];
  color: string;
  dashed: boolean;
  filled: boolean;
}

export interface TrendChartModel {
  timestamps: number[];
  /** Per-slot tooltip title. Empty when several sources share the axis and no single name applies. */
  pointLabels: string[];
  datasets: TrendDataset[];
}

const isUsableTimestamp = (point: TrendPoint) =>
  point.timestamp != null && Number.isFinite(Number(point.timestamp));

const datedPoints = (source: TrendSource) =>
  (source.points || []).filter(isUsableTimestamp);

/** How many points of a series can actually be plotted — a value AND a place to put it. */
export const countPlottable = (points: TrendPoint[], benchmarkKey?: string) =>
  (points || []).filter(
    (point) =>
      isUsableTimestamp(point) &&
      (benchmarkKey
        ? point.benchmarks?.[benchmarkKey] != null
        : point.primary != null),
  ).length;

/**
 * A source earns a chip only if it can draw a LINE. One point is a dot, not a trend, and a chip that
 * opens onto an empty chart is worse than no chip.
 *
 * <p>This counts INPUT points while {@link modelHasTrend} counts output slots; the two agree only
 * because every plottable point is guaranteed its own slot (see {@link buildTrendChartModel}). If
 * slots were ever collapsed again, a source could earn a chip and then open onto the empty state.
 */
export const isDrawableSource = (source: TrendSource) =>
  countPlottable(source?.points || []) >= 2;

/**
 * Builds the category-axis model for the visible sources.
 *
 * The x axis is the UNION of every visible source's dates, sorted ascending, with each series
 * null-padded into its slots — so overlaid sources stay aligned to real chronology even when they
 * were assessed on different days. Nulls are preserved end-to-end (the chart spans gaps): a missing
 * score means "not assessed", and rendering it as 0 would draw a collapse that never happened.
 *
 * Benchmarks are emitted only in single-source view; overlaying four sources' benchmarks would put
 * eight lines on one chart and stop it being readable.
 */
export const buildTrendChartModel = (
  visibleSources: TrendSource[],
  /** Whether anything OUTSIDE the legend names the source — in practice, whether the source-selector
   *  chip strip is on screen. `ScoreTrendChart` renders that strip only when more than one source is
   *  drawable, so a learner or branch with just one assessed source has NO chip, no source in the
   *  title, and the legend as the only place it could appear. Labelling the line by scope there
   *  would leave the reader with "Şube puanı / Sınıf puanı" and nothing saying where the numbers
   *  came from. Defaults to false because that is the safe direction: keep naming the source. */
  sourceNamedElsewhere = false,
): TrendChartModel => {
  const sources = visibleSources || [];

  // Slots are keyed on (timestamp, occurrence), NOT on the timestamp alone. Distinct assessments
  // genuinely share an instant — a branch's assignments routinely share one `endAt` deadline, and a
  // mock exam emits one item per lesson all stamped with the exam's date — so one slot per
  // date would drop real scores from a chart whose whole job is reporting history, and would leave
  // the chart contradicting the score table on the same tab. Each timestamp therefore reserves as
  // many adjacent slots as the busiest source needs, and every point gets its own. The axis spaces
  // points evenly anyway, so this is visually free.
  const occurrencesByTimestamp = new Map<number, number>();
  sources.forEach((source) => {
    const perSource = new Map<number, number>();
    datedPoints(source).forEach((point) => {
      const timestamp = Number(point.timestamp);
      perSource.set(timestamp, (perSource.get(timestamp) ?? 0) + 1);
    });
    perSource.forEach((count, timestamp) => {
      occurrencesByTimestamp.set(
        timestamp,
        Math.max(occurrencesByTimestamp.get(timestamp) ?? 0, count),
      );
    });
  });

  if (occurrencesByTimestamp.size === 0) {
    return { timestamps: [], pointLabels: [], datasets: [] };
  }

  const timestamps: number[] = [];
  const firstSlotByTimestamp = new Map<number, number>();
  Array.from(occurrencesByTimestamp.keys())
    .sort((a, b) => a - b)
    .forEach((timestamp) => {
      firstSlotByTimestamp.set(timestamp, timestamps.length);
      const slotCount = occurrencesByTimestamp.get(timestamp) ?? 1;
      for (let occurrence = 0; occurrence < slotCount; occurrence += 1) {
        timestamps.push(timestamp);
      }
    });

  /** Walks a source's dated points in order, handing each the next free slot for its timestamp. */
  const forEachPointSlot = (
    source: TrendSource,
    visit: (index: number, point: TrendPoint) => void,
  ) => {
    const nextOccurrence = new Map<number, number>();
    datedPoints(source).forEach((point) => {
      const timestamp = Number(point.timestamp);
      const occurrence = nextOccurrence.get(timestamp) ?? 0;
      nextOccurrence.set(timestamp, occurrence + 1);
      const firstSlot = firstSlotByTimestamp.get(timestamp);
      if (firstSlot != null) visit(firstSlot + occurrence, point);
    });
  };

  const singleSource = sources.length === 1;

  // With one source visible, each slot is that assignment/exam and can be named. Overlaid, several
  // sources can land in one slot, so the date carries identity and the per-series rows say which.
  const pointLabels: string[] = timestamps.map(() => "");
  if (singleSource) {
    forEachPointSlot(sources[0], (index, point) => {
      pointLabels[index] = point.label;
    });
  }

  const datasets: TrendDataset[] = [];
  sources.forEach((source) => {
    const primaryData: (number | null)[] = timestamps.map(() => null);
    forEachPointSlot(source, (index, point) => {
      primaryData[index] = point.primary;
    });
    datasets.push({
      // Alone on the axis AND named by the chip strip, the line says whose score it is — otherwise
      // "Şube ödevleri" sits next to "Şube puanı" meaning two different things. Sharing the axis,
      // source identity is the only thing distinguishing the lines; with no chip strip, the legend
      // is the only thing naming the source at all. Both of those need `label`.
      label:
        (singleSource && sourceNamedElsewhere && source.primaryLabel) ||
        source.label,
      data: primaryData,
      color: source.color,
      dashed: false,
      filled: singleSource,
    });

    if (!singleSource) return;
    (source.benchmarkDefs || []).forEach((benchmark) => {
      if (countPlottable(source.points, benchmark.key) < 2) return;
      const benchmarkData: (number | null)[] = timestamps.map(() => null);
      forEachPointSlot(source, (index, point) => {
        benchmarkData[index] = point.benchmarks?.[benchmark.key] ?? null;
      });
      datasets.push({
        label: benchmark.label,
        data: benchmarkData,
        color: benchmark.color,
        dashed: true,
        filled: false,
      });
    });
  });

  return { timestamps, pointLabels, datasets };
};

/** True when at least one series has enough real points to be a line rather than a dot. */
export const modelHasTrend = (model: TrendChartModel) =>
  (model?.datasets || []).some(
    (dataset) => dataset.data.filter((value) => value != null).length >= 2,
  );
